import React, { useState, useEffect, useCallback } from 'react'
import styled, { withTheme } from 'styled-components'
import { inject, observer } from 'mobx-react'
import ProductGallery from '../components/ProductGallery/ProductGallery'
import ProductBuyBox from '../components/ProductBuyBox/ProductBuyBox'
import { Spinner } from 'react-bootstrap'
import ProductDetail from '../components/ProductDetail/ProductDetail'
import StickyShoppingBasket from '../components/StickyShoppingBasket/StickyShoppingBasket'
import { withTranslation } from 'react-i18next'
import { Subheader } from '../components/Page/logix2020/Subheader'
import SubheaderTitle from '../components/Employees/Subheader/SubheaderTitle'
import EmployeeData from '../components/Employees/Subheader/EmployeeData'
import { Navigate, useParams } from 'react-router-dom'
import OrderableAccessories from '../components/ProductDetail/OrderableAccessories'
import { ContentArea } from '../components/Page/logix2020/ContentArea'
import BigFooter from '../components/Footer/BigFooter'
import AddOrderableAccessoriesModal from '../components/ProductBuyBox/OrderableAccessories/AddOrderableAccessoriesModal'

const StyledContentArea = styled(ContentArea)`
  background-color: #f8fafc;
  min-height: 100vh;
  padding-bottom: 60px;
`
const StyledContainer = styled.div`
  padding: 40px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03);
  margin-top: 24px;
`

const StyledContentWrapper = styled.div`
  max-width: 1440px;
  margin: 0 auto;
`

const AccessorySidebarWrapper = styled.div`
  border-left: 1px solid #e2e8f0;
  padding-left: 32px;
  height: 100%;
`
const StyledLoading = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  overflow: hidden;
  position: relative;
  height: calc(100vh - 237px);
`
const StyledLoadingText = styled.span`
  color: ${(props) => props.theme.primaryColor};
  font-size: 16px;
  letter-spacing: -1px;
  margin-left: 20px;
  text-transform: uppercase;
`

const ProductDetailsPage = inject(
  'productsStore',
  'teamOrderStore',
  'profileStore',
  'productDetailsStore'
)(
  observer((props) => {
    const [redirectToProductsOverview] = useState(false)
    const {
      employeeId,
      shoppingBasketArticleId,
      costCenterId,
      storeHouseId,
      orderType,
    } = useParams()
    const {
      t,
      productsStore,
      teamOrderStore,
      profileStore,
      productDetailsStore,
    } = props
    const initPrdoductDetailPage = useCallback(async () => {
      try {
        await productsStore.loadOrCreateBasket()
        await productDetailsStore.loadShoppingBasketArticle(
          shoppingBasketArticleId
        )
        await productDetailsStore.loadShoppingBasketArticleNextExpiries(
          shoppingBasketArticleId
        )
        await productDetailsStore.loadArticle(
          productDetailsStore.shoppingBasketArticle.article.id
        )
      } catch (e) {
        console.error('Failed to initialize product detail page:', e)
      }
    }, [productDetailsStore, productsStore, shoppingBasketArticleId])

    useEffect(() => {
      teamOrderStore.initializeCurrentProcessType(
        storeHouseId,
        costCenterId,
        orderType
      )
      //get + set employee ID depending on params or logged in user
      const newEmployeeId =
        employeeId || (!!costCenterId ? '' : profileStore.profile.id)
      if (productsStore.targetEmployeeId !== newEmployeeId) {
        productsStore.targetEmployeeId = newEmployeeId
      }
      initPrdoductDetailPage()
      return () => {
        productDetailsStore.cancelLoadAarticle()
        productDetailsStore.resetBuyBoxState()
      }
    }, [
      storeHouseId,
      costCenterId,
      employeeId,
      initPrdoductDetailPage,
      orderType,
      productDetailsStore,
      productsStore,
      profileStore.profile.id,
      shoppingBasketArticleId,
      teamOrderStore,
    ])

    const article = productDetailsStore.article
    const shoppingBasketArticle = productDetailsStore.shoppingBasketArticle
    const toProductsOverview =
      productsStore.targetEmployeeId === profileStore.profile.id
        ? '/products/personal'
        : `/products/personal/${productsStore.targetEmployeeId}`
    const galleryImages = productDetailsStore.getGalleryImages() || []
    const processType = teamOrderStore.getCurrentProcessType()

    if (redirectToProductsOverview) return <Navigate to={toProductsOverview} />

    const getLinkToProductsOverview = () => {
      switch (processType) {
        case 'storeHouse':
          return `/storehouse/${teamOrderStore.selectedStorehouseId}/${teamOrderStore.selectedCostcenterId}/products`
        case 'costCenter':
          return `/costcenter/${teamOrderStore.selectedCostcenterId}/costcenterproducts`
        case 'private':
          return `/products/private`
        case 'asset':
          return `/products/asset/${productsStore.targetEmployeeId}`
        case 'personal':
        default:
          return toProductsOverview
      }
    }

    if (!article || !shoppingBasketArticle) {
      return (
        <StyledLoading>
          <Spinner animation='border' variant='primary' />
          <StyledLoadingText>
            {t('productDetail:loadingProduct')}
          </StyledLoadingText>
        </StyledLoading>
      )
    }

    return (
      <div>
        <Subheader className='subHeader-v1'>
          {processType === 'personal' ? (
            <>
              <div className='flex-grow-1'>
                <SubheaderTitle
                  withBackButton
                  defaultBackTo={getLinkToProductsOverview()}
                />
              </div>
              <EmployeeData showJobFunction />
            </>
          ) : (
            <SubheaderTitle
              withBackButton
              defaultBackTo={getLinkToProductsOverview()}
              goBackLabel={t('productDetail:backToProductsOverview')}
            />
          )}
        </Subheader>

        <StyledContentArea>
          <StyledContentWrapper className='container-fluid'>
            <StyledContainer>
              <div className='row g-5'>
                <div className='col-xl-9'>
                  <div className='row g-4'>
                    <div className='col-md-6'>
                      <ProductGallery
                        article={article}
                        images={galleryImages}
                      />
                    </div>
                    <div className='col-md-6'>
                      <ProductBuyBox />
                    </div>
                  </div>

                  <div style={{ marginTop: '56px' }}>
                    <ProductDetail
                      article={article}
                      processType={processType}
                      images={galleryImages}
                    />
                  </div>
                </div>

                <div className='col-xl-3'>
                  <AccessorySidebarWrapper>
                    {productsStore.basket.id && article.id && (
                      <OrderableAccessories
                        basketId={productsStore.basket.id}
                        orderType={orderType}
                        articleId={article.id}
                        costCenterId={costCenterId || null}
                        employeeId={employeeId}
                      />
                    )}
                  </AccessorySidebarWrapper>
                </div>
              </div>
            </StyledContainer>
            <StickyShoppingBasket />
          </StyledContentWrapper>
        </StyledContentArea>

        <BigFooter />

        {productDetailsStore.addOrderableAccessoriesModal && (
          <AddOrderableAccessoriesModal
            basketId={productsStore.basket.id}
            shoppingBasketArticleId={shoppingBasketArticle.id}
            selectedVariantId={productDetailsStore.selectedVariantId}
            selectedQuantity={productDetailsStore.selectedQuantity}
            orderableAccessories={productDetailsStore.addOrderableAccessories}
            handleClose={(submit) => {
              productDetailsStore.setAddOrderableAccessoriesModal(false)
              if (submit) {
                productDetailsStore.handleBasketUpdate()
              }
            }}
          />
        )}
      </div>
    )
  })
)

export default withTheme(withTranslation()(ProductDetailsPage))
